import type { FC, ReactNode } from "react";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";

import Breadcrumbs from "@/components/Breadcrumb";
import { PageSuspense } from "@/components/PageSuspense";
import { nonSettingsPageNavigationEntryPredicate } from "@/components/settings/settings";
import { SettingsPageContext, type BreadcrumbItem } from "@/contexts/SettingsPageContext";
import { useEscapeShortcut } from "@/hooks/use-escape-shortcut";
import { useLastNavigationEntry } from "@/hooks/use-last-navigation-entry";
import { useSidebarController } from "@/hooks/use-sidebar-controller";

export const SettingsPageLayout: FC = () => {
    const navigate = useNavigate();
    const sidebarController = useSidebarController();
    const [breadCrumbRowAction, setBreadCrumbRowAction] = useState<ReactNode | null>(null);
    const [customBreadcrumbs, setCustomBreadcrumbs] = useState<BreadcrumbItem[] | null>(null);
    const lastVisitedNonSettingsPage = useLastNavigationEntry(nonSettingsPageNavigationEntryPredicate);

    useEscapeShortcut(() => {
        navigate(lastVisitedNonSettingsPage);
    });

    useEffect(() => {
        sidebarController.lockSidebar();
    }, [sidebarController]);

    const showHeader = customBreadcrumbs === null || customBreadcrumbs.length > 0;

    return (
        <div className="flex flex-col gap-2" data-testid="settings-page-layout">
            {showHeader && (
                <div className="flex flex-row items-center justify-between gap-2">
                    <Breadcrumbs customBreadcrumbs={customBreadcrumbs} size="large" />
                    {breadCrumbRowAction}
                </div>
            )}
            <SettingsPageContext.Provider value={{ setBreadCrumbRowAction, setCustomBreadcrumbs }}>
                <PageSuspense>
                    <Outlet />
                </PageSuspense>
            </SettingsPageContext.Provider>
        </div>
    );
};
